"use client";
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Link from "next/link";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

const AppIcon = ({ app }) => {
  const [src, setSrc] = useState(`/odoo/icons/apps/${app.icon}.svg?v=2`);

  useEffect(() => {
    setSrc(`/odoo/icons/apps/${app.icon}.svg?v=2`);
  }, [app.icon]);

  return (
    <Image
      src={src}
      width={68}
      height={68}
      className="rounded bg-white/5 p-2 shadow-sm object-contain"
      alt={`Odoo ${app.name} icon`}
      onError={() => {
        setSrc("/odoo/icons/green_thumbs-up.svg");
      }}
    />
  );
};

export default function IndustryAppGrid({ data = {} }) {
  const relatedApps = data?.relatedApps || [];
  if (!relatedApps.length) return null;

  return (
    <section className="bg-white/5/10/80 py-20 px-6 sm:px-12 lg:px-24">
      <div className="container mx-auto">
        <h3 className={`mb-4 text-4xl sm:text-5xl md:text-6xl leading-[0.95] text-white ${caveat.className}`}>
          One <span className="relative inline-block">
            <span className="relative z-10">need</span>
            <span className="absolute -bottom-1 left-0 h-2 w-full rounded-full bg-orange-400/90 rotate-[-2deg]" />
          </span>, one <span className="relative inline-block">
            <span className="relative z-10">app</span>
            <span className="absolute -bottom-1 left-0 h-2 w-full rounded-full bg-orange-400/90 rotate-[-2deg]" />
          </span>.
        </h3>
        <p className="mb-10 text-xl text-gray-300">Expand as you grow.</p>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {relatedApps.map((app, i) => (
            <motion.div 
              key={i}
              whileHover={{ y: -3 }}
              className="relative h-full rounded-md border border-white/20 bg-white/5/90 transition-all hover:bg-white/5 hover:shadow-md cursor-pointer"
            >
              <Link href={`/odoo/app/${app.icon}`} className="absolute inset-0 z-20" aria-label={app.name} />
              <div className="flex items-center gap-4 p-4 sm:p-5">
                <AppIcon app={app} />
                <div>
                  <h5 className="mb-1 text-xl font-semibold leading-none text-white sm:text-2xl">
                    {app.name}
                  </h5>
                  <small className="text-base text-gray-400 sm:text-lg">{app.desc}</small>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <a
          href="https://www.odoo.com/page/all-apps"
          target="_blank"
          rel="noopener noreferrer"
          className={`mt-10 inline-flex items-center gap-3 text-4xl text-[#f1833b] ${caveat.className}`}
        >
          See all Apps
          <Image
            src="/odoo/arrows/secondary_arrow_sm_03.svg"
            width={40}
            height={40}
            className="align-baseline object-contain"
            alt=""
          />
        </a>
      </div>
    </section>
  );
}
